import {
  type ApiSpecificSales,
  type ApiUniverseSales,
  type Period,
  SalesDistribution,
  type SalesUniverse,
  type SpecificUniverseName,
  type UniverseName,
  assertNever,
  specificUniverseEnum,
  universeEnum,
  useSetFilter,
} from "@pharmupp/p3-front-commons"
import { SalesChips, type SalesChipsProps } from "../../../Sales"
import { SALES_FILTERS_NAME } from "./salesSection.type"

const getByDistribution = ({
  universe,
  distribution,
}: {
  universe: SalesUniverse
  distribution: SalesDistribution
}): Pick<SalesChipsProps, "value" | "evolution" | "globalEvolution"> => {
  switch (distribution) {
    case SalesDistribution.SALES:
      return {
        value: universe.sales?.mainStat,
        evolution: universe.sales?.evolution,
        globalEvolution: universe.uppSales?.evolution,
      }
    case SalesDistribution.MARGIN:
      return {
        value: universe.margin?.mainStat,
        evolution: universe.margin?.evolution,
        globalEvolution: universe.uppMargin?.evolution,
      }
    default:
      return assertNever(
        distribution,
        { value: null, evolution: null, globalEvolution: null },
        "getByDistribution",
      )
  }
}

export const UniversesChips = ({
  universes,
  distribution,
  period,
}: {
  universes: ApiUniverseSales["universes"]
  distribution: SalesDistribution
  period: Period
}) => {
  const setFilters = useSetFilter()

  const navigate = (universeCode: UniverseName) =>
    setFilters(
      [
        { filterName: SALES_FILTERS_NAME.DISTRIBUTION, filterValue: distribution },
        { filterName: SALES_FILTERS_NAME.PERIOD, filterValue: period },
      ],
      `./univers/${encodeURIComponent(universeCode)}`,
    )
  return (
    <>
      {universes.map((universe) => {
        const { value, evolution, globalEvolution } = getByDistribution({
          universe,
          distribution,
        })
        return (
          <SalesChips
            key={universe.name}
            title={universeEnum[universe.name].label}
            Icon={universeEnum[universe.name].Icon}
            value={value}
            evolution={evolution}
            globalEvolution={globalEvolution}
            onClick={() => navigate(universe.name)}
          />
        )
      })}
    </>
  )
}

export const SpecificChips = ({
  categories,
  distribution,
  period,
}: {
  categories: ApiSpecificSales["specificCategories"]
  distribution: SalesDistribution
  period: Period
}) => {
  const setFilters = useSetFilter()

  const navigate = (specificCode: SpecificUniverseName) =>
    setFilters(
      [
        { filterName: SALES_FILTERS_NAME.DISTRIBUTION, filterValue: distribution },
        { filterName: SALES_FILTERS_NAME.PERIOD, filterValue: period },
      ],
      `./marche-specifique/${encodeURIComponent(specificCode)}`,
    )
  return (
    <>
      {categories.map((category) => {
        const { value, evolution, globalEvolution } = getByDistribution({
          universe: category,
          distribution,
        })
        return (
          <SalesChips
            key={category.name}
            title={specificUniverseEnum[category.name].label}
            Icon={specificUniverseEnum[category.name].Icon}
            value={value}
            evolution={evolution}
            globalEvolution={globalEvolution}
            onClick={() => navigate(category.name)}
          />
        )
      })}
    </>
  )
}
